import { useState } from "react";
import "./AgriSimContact.css";

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  requesterType: "",
  organizationName: "",
  numberOfTrainees: "",
  message: "",
};

export default function AgriSimContact() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (!form.phone.trim()) e.phone = "Phone is required";
    if (!form.requesterType) e.requesterType = "Please select one";
    if (form.requesterType === "rpto" && !form.organizationName.trim())
      e.organizationName = "Organisation name is required";
    if (!form.message.trim()) e.message = "Tell us what you're looking for";
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: undefined }));
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    setStatus("loading");
    try {
      const res = await fetch("/api/agrisim/demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed");
      setStatus("success");
      setForm(INITIAL);
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <section className="asc" id="request-demo">
        <div className="asc__bg-grid" />
        <div className="asc__wrap">
          <div className="asc__success">
            <div className="asc__success-icon">
              <svg viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="1.5" />
                <path d="M14 24l7 7 13-13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3 className="asc__success-title">Request Received</h3>
            <p className="asc__success-body">
              Thank you — we've received your demo request and sent a confirmation to your email. Our team will be in touch within 1–2 business days to schedule your session.
            </p>
            <button className="asc__success-back" onClick={() => setStatus("idle")}>
              Submit another request
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="asc" id="request-demo">
      <div className="asc__bg-grid" aria-hidden="true" />

      <div className="asc__wrap">

        {/* ── Left column ── */}
        <div className="asc__left">
          <div className="asc__eyebrow">
            <span className="asc__eyebrow-dot" />
            <span className="asc__eyebrow-line" />
            <span className="asc__eyebrow-text">Book a Demo</span>
          </div>

          <h2 className="asc__heading">
            See AgroFlyte
            <em> in action.</em>
          </h2>

          <p className="asc__lead">
            Whether you're an RPTO looking to train your next batch of pilots, or a drone enthusiast ready to get serious — book a private demo session and experience the simulation firsthand.
          </p>

          <div className="asc__perks">
            {[
              { icon: "◈", label: "Live physics demo", sub: "Full drone flight walkthrough" },
              { icon: "◉", label: "Taranis QX7 in hand", sub: "Same transmitter, real feel" },
              { icon: "◆", label: "Tailored for your need", sub: "RPTO batch or solo session" },
              { icon: "◇", label: "No commitment needed", sub: "Just request and we'll reach out" },
            ].map((p) => (
              <div className="asc__perk" key={p.label}>
                <span className="asc__perk-icon">{p.icon}</span>
                <div>
                  <p className="asc__perk-label">{p.label}</p>
                  <p className="asc__perk-sub">{p.sub}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="asc__contact-info">
            <a href="mailto:support@aviatricks.in" className="asc__contact-link">
              support@aviatricks.in
            </a>
          </div>
        </div>

        {/* ── Right column — form ── */}
        <div className="asc__right">
          <div className="asc__form-card">

            <div className="asc__form-header">
              <span className="asc__form-title">Request a Demo</span>
              <span className="asc__form-sub">We'll get back within 1–2 business days</span>
            </div>

            {/* Requester type toggle */}
            <div className="asc__field">
              <label className="asc__label">I am a <span className="asc__req">*</span></label>
              <div className="asc__toggle-group">
                {[
                  { value: "rpto", label: "RPTO / Organisation" },
                  { value: "individual", label: "Individual Enthusiast" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`asc__toggle ${form.requesterType === opt.value ? "asc__toggle--active" : ""}`}
                    onClick={() => {
                      setForm((p) => ({ ...p, requesterType: opt.value }));
                      setErrors((p) => ({ ...p, requesterType: undefined }));
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {errors.requesterType && <span className="asc__error">{errors.requesterType}</span>}
            </div>

            {/* Row: Name + Phone */}
            <div className="asc__row">
              <div className="asc__field">
                <label className="asc__label">Full Name <span className="asc__req">*</span></label>
                <input
                  className={`asc__input ${errors.name ? "asc__input--err" : ""}`}
                  name="name" value={form.name} onChange={handleChange}
                  placeholder="Your name"
                />
                {errors.name && <span className="asc__error">{errors.name}</span>}
              </div>
              <div className="asc__field">
                <label className="asc__label">Phone <span className="asc__req">*</span></label>
                <input
                  className={`asc__input ${errors.phone ? "asc__input--err" : ""}`}
                  name="phone" value={form.phone} onChange={handleChange}
                  placeholder="+91 00000 00000"
                />
                {errors.phone && <span className="asc__error">{errors.phone}</span>}
              </div>
            </div>

            {/* Email */}
            <div className="asc__field">
              <label className="asc__label">Email Address <span className="asc__req">*</span></label>
              <input
                className={`asc__input ${errors.email ? "asc__input--err" : ""}`}
                name="email" type="email" value={form.email} onChange={handleChange}
                placeholder="you@example.com"
              />
              {errors.email && <span className="asc__error">{errors.email}</span>}
            </div>

            {/* RPTO-only fields */}
            {form.requesterType === "rpto" && (
              <div className="asc__row asc__rpto-fields">
                <div className="asc__field">
                  <label className="asc__label">Organisation Name <span className="asc__req">*</span></label>
                  <input
                    className={`asc__input ${errors.organizationName ? "asc__input--err" : ""}`}
                    name="organizationName" value={form.organizationName} onChange={handleChange}
                    placeholder="Your RPTO / Academy name"
                  />
                  {errors.organizationName && <span className="asc__error">{errors.organizationName}</span>}
                </div>
                <div className="asc__field">
                  <label className="asc__label">No. of Trainees</label>
                  <input
                    className="asc__input"
                    name="numberOfTrainees" type="number" min="1"
                    value={form.numberOfTrainees} onChange={handleChange}
                    placeholder="e.g. 20"
                  />
                </div>
              </div>
            )}

            {/* Message */}
            <div className="asc__field">
              <label className="asc__label">What are you looking for? <span className="asc__req">*</span></label>
              <textarea
                className={`asc__textarea ${errors.message ? "asc__input--err" : ""}`}
                name="message" value={form.message} onChange={handleChange}
                placeholder="Tell us about your training goals, batch size, timeline, or anything else..."
                rows={4}
              />
              {errors.message && <span className="asc__error">{errors.message}</span>}
            </div>

            {status === "error" && (
              <p className="asc__form-error">
                Something went wrong. Please try again or email us directly.
              </p>
            )}

            <button
              className="asc__submit"
              onClick={handleSubmit}
              disabled={status === "loading"}
            >
              {status === "loading" ? (
                <span className="asc__spinner" />
              ) : (
                <>
                  <span>Book Your Demo</span>
                  <svg viewBox="0 0 20 20" fill="none" className="asc__submit-arrow">
                    <path d="M4 10h12M11 5l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </>
              )}
            </button>

            <p className="asc__disclaimer">
              No spam. No commitment. We'll only reach out to schedule your demo.
            </p>

          </div>
        </div>

      </div>
    </section>
  );
}